/**
 * Storage utilities for persisting data across Safari and PWA on iOS
 * Uses IndexedDB which is shared between Safari and standalone PWA
 */

const DB_NAME = 'paulownia';
const STORE_NAME = 'settings';
const DB_VERSION = 1;

let dbPromise: Promise<IDBDatabase> | null = null;

const DB_TIMEOUT_MS = 10000; // 10 seconds timeout for IndexedDB operations

function getDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    // Timeout to prevent indefinite blocking on problematic devices
    const timeoutId = setTimeout(() => {
      dbPromise = null; // Reset cache to allow retry
      reject(new Error('IndexedDB timeout'));
    }, DB_TIMEOUT_MS);

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      clearTimeout(timeoutId);
      dbPromise = null; // Reset cache on error
      reject(request.error);
    };

    request.onsuccess = () => {
      clearTimeout(timeoutId);
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });

  return dbPromise;
}

export async function saveTenantSlug(slug: string): Promise<void> {
  try {
    // Save to localStorage (works on Android/Desktop)
    localStorage.setItem('pwa_tenant_slug', slug);

    // Also save to IndexedDB (works on iOS)
    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    store.put(slug, 'tenant_slug');

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    console.error('Error saving tenant slug:', error);
  }
}

export async function getTenantSlug(): Promise<string | null> {
  try {
    // Try localStorage first (faster)
    const localStorageSlug = localStorage.getItem('pwa_tenant_slug');
    if (localStorageSlug) {
      return localStorageSlug;
    }

    // Fallback to IndexedDB (for iOS)
    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get('tenant_slug');

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting tenant slug:', error);
    return null;
  }
}

export async function clearTenantSlug(): Promise<void> {
  try {
    // Clear from localStorage
    localStorage.removeItem('pwa_tenant_slug');

    // Clear from IndexedDB
    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    store.delete('tenant_slug');

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    console.error('Error clearing tenant slug:', error);
  }
}

/**
 * Favorites management
 */

export async function getFavorites(): Promise<string[]> {
  try {
    // Try localStorage first (faster)
    const localStorageFavorites = localStorage.getItem('pwa_favorites');
    if (localStorageFavorites) {
      return JSON.parse(localStorageFavorites);
    }

    // Fallback to IndexedDB (for iOS)
    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get('favorites');

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting favorites:', error);
    return [];
  }
}

export async function addFavorite(slug: string): Promise<void> {
  try {
    const favorites = await getFavorites();
    if (!favorites.includes(slug)) {
      favorites.push(slug);

      // Save to localStorage
      localStorage.setItem('pwa_favorites', JSON.stringify(favorites));

      // Save to IndexedDB
      const db = await getDB();
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      store.put(favorites, 'favorites');

      return new Promise((resolve, reject) => {
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      });
    }
  } catch (error) {
    console.error('Error adding favorite:', error);
  }
}

export async function removeFavorite(slug: string): Promise<void> {
  try {
    const favorites = await getFavorites();
    const filtered = favorites.filter(f => f !== slug);

    // Save to localStorage
    localStorage.setItem('pwa_favorites', JSON.stringify(filtered));

    // Save to IndexedDB
    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    store.put(filtered, 'favorites');

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    console.error('Error removing favorite:', error);
  }
}

/**
 * Local Order Tracking
 * Store order references locally for customer order history
 */

export interface LocalOrderInfo {
  orderId: string;
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  marketId?: string;
  createdAt: string;  // ISO date
}

export async function saveLocalOrder(order: LocalOrderInfo): Promise<void> {
  try {
    const orders = await getLocalOrders();
    // Keep only last 20 orders, add new one at the beginning
    const updatedOrders = [order, ...orders.filter(o => o.orderId !== order.orderId)].slice(0, 20);

    // Save to localStorage
    localStorage.setItem('pwa_local_orders', JSON.stringify(updatedOrders));

    // Save to IndexedDB
    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    store.put(updatedOrders, 'local_orders');

    await new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    // Non-critical storage - log but don't propagate to caller
    console.error('Error saving local order:', error);
  }
}

export async function getLocalOrders(): Promise<LocalOrderInfo[]> {
  try {
    // Try localStorage first (faster)
    const localStorageOrders = localStorage.getItem('pwa_local_orders');
    if (localStorageOrders) {
      return JSON.parse(localStorageOrders);
    }

    // Fallback to IndexedDB (for iOS)
    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get('local_orders');

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting local orders:', error);
    return [];
  }
}

export async function setLocalOrders(orders: LocalOrderInfo[]): Promise<void> {
  try {
    // Save to localStorage
    localStorage.setItem('pwa_local_orders', JSON.stringify(orders));

    // Save to IndexedDB
    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    store.put(orders, 'local_orders');

    await new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    // Non-critical storage - log but don't propagate to caller
    console.error('Error setting local orders:', error);
  }
}

/**
 * Last Market Info
 * Store the last visited market for quick access
 */

export interface LastMarketInfo {
  marketId: string;
  marketCity: string;
  visitedAt: string;  // ISO date
}

export async function saveLastMarket(market: LastMarketInfo): Promise<void> {
  try {
    // Save to localStorage
    localStorage.setItem('pwa_last_market', JSON.stringify(market));

    // Save to IndexedDB
    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    store.put(market, 'last_market');

    await new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    // Non-critical storage - log but don't propagate to caller
    console.error('Error saving last market:', error);
  }
}

export async function getLastMarket(): Promise<LastMarketInfo | null> {
  try {
    // Try localStorage first (faster)
    const localStorageMarket = localStorage.getItem('pwa_last_market');
    if (localStorageMarket) {
      return JSON.parse(localStorageMarket);
    }

    // Fallback to IndexedDB (for iOS)
    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get('last_market');

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting last market:', error);
    return null;
  }
}

/**
 * Current Market
 * Global market context for the customer app.
 * Persisted so the user doesn't have to re-select on every visit.
 */

export interface CurrentMarketInfo {
  marketId: string;
  city: string;
  dayOfWeek: number;  // ISO 8601: 1-7
}

export async function saveCurrentMarket(market: CurrentMarketInfo): Promise<void> {
  try {
    localStorage.setItem('pwa_current_market', JSON.stringify(market));

    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    store.put(market, 'current_market');

    await new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    console.error('Error saving current market:', error);
  }
}

export async function getCurrentMarket(): Promise<CurrentMarketInfo | null> {
  try {
    const stored = localStorage.getItem('pwa_current_market');
    if (stored) {
      return JSON.parse(stored);
    }

    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get('current_market');

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting current market:', error);
    return null;
  }
}

export async function clearCurrentMarket(): Promise<void> {
  try {
    localStorage.removeItem('pwa_current_market');

    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    store.delete('current_market');

    await new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    console.error('Error clearing current market:', error);
  }
}

/**
 * Multi-merchant Carts
 * Each merchant has its own cart tied to a specific market.
 * Carts are independent - changing market for one merchant doesn't affect others.
 */

export interface StoredCartItem {
  productId: string;
  productName: string;
  quantity: number;
  price?: number;
  options: Array<{ id: string; value: boolean | string | string[] | Array<{ productId: string; quantity: number }> }>;
  portionSizeId?: string;
  portionSizeLabel?: string;
  saleMode?: string;
  servings?: number;
}

export interface MerchantCart {
  merchantId: string;
  merchantSlug: string;
  merchantName: string;
  marketId: string;
  marketCity: string;
  marketDayOfWeek: number;
  items: StoredCartItem[];
}

export async function saveMerchantCarts(carts: MerchantCart[]): Promise<void> {
  try {
    localStorage.setItem('pwa_merchant_carts', JSON.stringify(carts));

    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    store.put(carts, 'merchant_carts');

    await new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  } catch (error) {
    console.error('Error saving merchant carts:', error);
  }
}

export async function getMerchantCarts(): Promise<MerchantCart[]> {
  try {
    const stored = localStorage.getItem('pwa_merchant_carts');
    if (stored) {
      return JSON.parse(stored);
    }

    const db = await getDB();
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get('merchant_carts');

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting merchant carts:', error);
    return [];
  }
}
